import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PageRenderer from "./PageRenderer";
import { companyDetails } from "../data";
import { Calendar, User, Phone, Mail, MapPin, Lock, CheckCircle, AlertCircle } from "lucide-react";

export default function Holdstart() {
  const [formData, setFormData] = useState({
    holdstart: "",
    navn: "",
    telefon: "",
    cpr: "",
    email: "",
    bekraeftEmail: "",
    adresse: "",
    postnr: "",
    by: "",
    besked: "",
    website_url: "" // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState("");
  const [dynamicHoldOptions, setDynamicHoldOptions] = useState([
    "Mandag d. 4 maj 2026",
    "Tirsdag d. 2 juni 2026",
    "TORSDAG d. 10 august 2026",
    "Tirsdag d. 1 september 2026",
    "Mandag d. 5 oktober 2026",
    "Tirsdag d. 3 november 2026",
    "Mandag d. 7 december 2026",
    "Tirsdag d. 5 januar 2027"
  ]);

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/holdstart")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (data && data.length > 0) {
          // Sort items by sortering if present
          const sorted = [...data].sort((a, b) => (a.sortering || 99) - (b.sortering || 99));
          const titles = sorted.map((item) => item.titel?.da || item.titel || item.title || "").filter(Boolean);
          if (titles.length > 0) {
            setDynamicHoldOptions(titles);
          }
        }
      })
      .catch(() => {});
  }, []);

  const holdOptions = dynamicHoldOptions;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.holdstart) newErrors.holdstart = "Vælg venligst et hold";
    if (!formData.navn.trim()) newErrors.navn = "Navn er påkrævet";
    if (!formData.telefon.trim()) newErrors.telefon = "Telefonnummer er påkrævet";
    if (!formData.cpr.trim()) newErrors.cpr = "CPR nr. er påkrævet";
    if (!formData.email.trim()) {
      newErrors.email = "E-mail er påkrævet";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ugyldig e-mailadresse";
    }
    if (!formData.bekraeftEmail.trim()) {
      newErrors.bekraeftEmail = "Bekræft e-mail er påkrævet";
    } else if (formData.email !== formData.bekraeftEmail) {
      newErrors.bekraeftEmail = "E-mailadresser matcher ikke";
    }
    if (!formData.adresse.trim()) newErrors.adresse = "Adresse er påkrævet";
    if (!formData.postnr.trim()) newErrors.postnr = "Postnr er påkrævet";
    if (!formData.by.trim()) newErrors.by = "By er påkrævet";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          holdstart: formData.holdstart,
          navn: formData.navn,
          telefon: formData.telefon,
          cpr: formData.cpr,
          email: formData.email,
          adresse: formData.adresse,
          postnr: formData.postnr,
          by: formData.by,
          besked: formData.besked,
          website_url: formData.website_url // Honeypot
        })
      });

      if (response.ok) {
        setSubmitStatus("success");
        setSubmitMessage("Mange tak for din tilmelding! Vi har modtaget dine oplysninger og vender tilbage til dig hurtigst muligt.");
        setFormData({
          holdstart: "",
          navn: "",
          telefon: "",
          cpr: "",
          email: "",
          bekraeftEmail: "",
          adresse: "",
          postnr: "",
          by: "",
          besked: "",
          website_url: ""
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        setSubmitStatus("error");
        setSubmitMessage(errData.message || "Der skete en fejl under indsendelsen. Prøv venligst igen eller kontakt os direkte.");
      }
    } catch (error) {
      console.error("Fejl ved indsendelse:", error);
      setSubmitStatus("error");
      setSubmitMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Render the standard Holdstart page content from CMS */}
      <PageRenderer customSlug="holdstart" />

      {/* Tilmeldingsformular Section */}
      <div className="bg-slate-50 py-16 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Form Column */}
            <div className="lg:col-span-8 bg-white p-8 md:p-10 rounded-2xl border border-slate-200 shadow-lg">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-slate-900 mb-2">Tilmelding til holdstart</h2>
                <p className="text-slate-600">Udfyld formularen nedenfor for at sikre dig en plads. Vi glæder os til at se dig!</p>
              </div>

              {submitStatus === "success" && (
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-lg mb-8 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-emerald-900 text-lg">Tilmelding modtaget!</h3>
                    <p className="text-emerald-800 mt-1">{submitMessage}</p>
                  </div>
                </div>
              )}

              {submitStatus === "error" && (
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8 flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-red-900 text-lg">Der opstod en fejl</h3>
                    <p className="text-red-800 mt-1">{submitMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field (absolutely hidden from humans) */}
                <div className="sr-only absolute -top-9999 -left-9999">
                  <label htmlFor="website_url">Efterlad dette felt tomt</label>
                  <input
                    id="website_url"
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                {/* Holdstart Selection */}
                <div>
                  <label htmlFor="holdstart" className="block text-sm font-bold text-slate-700 mb-1.5">
                    Vælg holdstart *
                  </label>
                  <div className="relative">
                    <select
                      id="holdstart"
                      name="holdstart"
                      value={formData.holdstart}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all appearance-none ${
                        errors.holdstart ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                      }`}
                    >
                      <option value="">-- Vælg holdstart --</option>
                      {holdOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                      <Calendar className="w-5 h-5" />
                    </div>
                  </div>
                  {errors.holdstart && <p className="text-red-500 text-xs font-semibold mt-1">{errors.holdstart}</p>}
                </div>

                {/* Personal Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Navn */}
                  <div>
                    <label htmlFor="navn" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Navn *
                    </label>
                    <div className="relative">
                      <input
                        id="navn"
                        type="text"
                        name="navn"
                        value={formData.navn}
                        onChange={handleChange}
                        placeholder="Dit fulde navn"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.navn ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <User className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.navn && <p className="text-red-500 text-xs font-semibold mt-1">{errors.navn}</p>}
                  </div>

                  {/* Telefonnummer */}
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Telefonnummer *
                    </label>
                    <div className="relative">
                      <input
                        id="telefon"
                        type="tel"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="Mobil eller fastnet"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.telefon ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Phone className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.telefon && <p className="text-red-500 text-xs font-semibold mt-1">{errors.telefon}</p>}
                  </div>

                  {/* CPR nr. */}
                  <div>
                    <label htmlFor="cpr" className="block text-sm font-bold text-slate-700 mb-1.5">
                      CPR nr. *
                    </label>
                    <div className="relative">
                      <input
                        id="cpr"
                        type="text"
                        name="cpr"
                        value={formData.cpr}
                        onChange={handleChange}
                        placeholder="DDMMYY-XXXX"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.cpr ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Lock className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.cpr && <p className="text-red-500 text-xs font-semibold mt-1">{errors.cpr}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Email *
                    </label>
                    <div className="relative">
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="din@email.dk"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.email ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Mail className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.email && <p className="text-red-500 text-xs font-semibold mt-1">{errors.email}</p>}
                  </div>

                  {/* Bekræft Email */}
                  <div className="md:col-span-2">
                    <label htmlFor="bekraeftEmail" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Bekræft Email *
                    </label>
                    <div className="relative">
                      <input
                        id="bekraeftEmail"
                        type="email"
                        name="bekraeftEmail"
                        value={formData.bekraeftEmail}
                        onChange={handleChange}
                        placeholder="Gentag din e-mail"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.bekraeftEmail ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <Mail className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.bekraeftEmail && <p className="text-red-500 text-xs font-semibold mt-1">{errors.bekraeftEmail}</p>}
                  </div>
                </div>

                {/* Address Details */}
                <div className="space-y-6">
                  {/* Adresse */}
                  <div>
                    <label htmlFor="adresse" className="block text-sm font-bold text-slate-700 mb-1.5">
                      Adresse *
                    </label>
                    <div className="relative">
                      <input
                        id="adresse"
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        placeholder="Vejnavn og husnummer"
                        className={`w-full pl-10 pr-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.adresse ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                        <MapPin className="w-5 h-5" />
                      </div>
                    </div>
                    {errors.adresse && <p className="text-red-500 text-xs font-semibold mt-1">{errors.adresse}</p>}
                  </div>

                  {/* Postnr & By Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Postnr */}
                    <div>
                      <label htmlFor="postnr" className="block text-sm font-bold text-slate-700 mb-1.5">
                        Postnr *
                      </label>
                      <input
                        id="postnr"
                        type="text"
                        name="postnr"
                        value={formData.postnr}
                        onChange={handleChange}
                        placeholder="F.eks. 8600"
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.postnr ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      {errors.postnr && <p className="text-red-500 text-xs font-semibold mt-1">{errors.postnr}</p>}
                    </div>

                    {/* By */}
                    <div>
                      <label htmlFor="by" className="block text-sm font-bold text-slate-700 mb-1.5">
                        By *
                      </label>
                      <input
                        id="by"
                        type="text"
                        name="by"
                        value={formData.by}
                        onChange={handleChange}
                        placeholder="F.eks. Silkeborg"
                        className={`w-full px-4 py-3 bg-white border rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all ${
                          errors.by ? "border-red-500 ring-2 ring-red-500/10" : "border-slate-300"
                        }`}
                      />
                      {errors.by && <p className="text-red-500 text-xs font-semibold mt-1">{errors.by}</p>}
                    </div>
                  </div>
                </div>

                {/* Besked */}
                <div>
                  <label htmlFor="besked" className="block text-sm font-bold text-slate-700 mb-1.5">
                    Skriv din besked (valgfri)
                  </label>
                  <textarea
                    id="besked"
                    name="besked"
                    rows="4"
                    value={formData.besked}
                    onChange={handleChange}
                    placeholder="Evt. kommentarer eller spørgsmål..."
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#00D492] hover:bg-[#00bf80] text-white font-bold text-base rounded-lg shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer"
                >
                  {isSubmitting ? "Sender tilmelding..." : "Send tilmelding"}
                </button>
              </form>
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-6">
              <div className="overflow-hidden bg-slate-950 text-white">
                <div className="relative h-64 sm:h-72">
                  <img
                    src="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/81acd7f6-8885-4245-9545-bff817c604ed.jpg"
                    alt="Audi Q8 E-tron"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                  <div className="absolute inset-0 flex items-end p-6">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">Nyhed i vognparken</p>
                      <h3 className="mt-2 text-2xl font-black uppercase tracking-tight">Audi Q8 E-tron</h3>
                    </div>
                  </div>
                </div>
                <div className="border-t border-white/10 p-6">
                  <p className="text-sm leading-7 text-white/75">
                    Vi arbejder med nyere skolevogne og et trygt, professionelt undervisningsmiljø.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
